<template>
  <v-card elevation="0" outlined min-height="350">
    <v-card-title class="pb-0">
      <v-icon color="secondary" large class="mr-2">lock</v-icon>
      <div class="d-flex flex-column">
        <span class="primary--text monospace-font body-1 font-weight-medium">{{ $t("table.usersOfClient.lastLogin") }}</span>
        <span class="subheader-font body-2">{{ $t("dashboards.offlineDaysOfClient") }}</span>
      </div>
    </v-card-title>
    <v-card-text class="d-flex flex-column">
      <v-row class="pa-0 ma-0 d-flex align-center justify-space-between mb-5">
        <!--Search-->
        <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 5" class="pa-0 ma-0">
          <v-text-field v-model="search" hide-details dense single-line label="Search" append-icon="search" class="ms-2">
            <template v-slot:label>
              <span class="body-1">{{ $t("general.search") }}</span>
            </template>
          </v-text-field>
        </v-col>
        <!--No connected-->
        <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 3" class="pa-0 ma-0">
          <v-checkbox @change="onInputChange()" hide-details v-model="hideNonLoggedIn">
            <template v-slot:label><span class="body-1">{{ $t("dashboards.hideNoConnected") }}</span></template>
          </v-checkbox>
        </v-col>
        <!--Only trial-->
        <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 3" class="pa-0 ma-0">
          <v-checkbox @change="onInputChange()" hide-details v-model="showOnlyTrial">
            <template v-slot:label><span class="body-1">{{ $t("dashboards.onlyTrial") }}</span></template>
          </v-checkbox>
        </v-col>
      </v-row>
      <v-data-table dense disable-items-per-page multi-sort
        :headers="lastLoginHeaders" 
        :items="filteredClientsData"
        :footer-props="{ itemsPerPageOptions: $vuetify.breakpoint.smAndDown ? [1] : [5]}"
        :search="search"
      >
        <!--Client-->
        <template v-slot:item.name="{ item }">
          <router-link :to="`/administration?clientId=${item.id}`">
            <span class="text-ellipsis body-1">{{ item.name }}</span>
          </router-link>
        </template>
        <!--User-->
        <template v-slot:item.username="{ item }">
          <router-link v-if="item.username" :to="`/user/${item.userId}`">
            <span class="text-ellipsis body-1">{{ getUsernameText(item) }}</span>
          </router-link>
          <span v-else>-</span>
        </template>
        <!--Last login-->
        <template v-slot:item.lastLogin="{ item }">
          <span v-if="item.lastLogin" class="text-ellipsis body-1">{{ item.lastLogin }}</span>
          <span class="text-ellipsis body-1" v-else>{{ $t("general.never") }}</span>
        </template>
        <!--Days without loggin in-->
        <template v-slot:item.daysPassed="{ item }">
          <span v-if="item.daysPassed" class="text-ellipsis body-1">{{ item.daysPassed }}</span>
          <span class="text-ellipsis body-1" v-else>-</span>
        </template>
        <!--Trial days-->
        <template v-slot:item.daysRemaining="{ item }">
          <span v-if="item.daysRemaining" class="text-ellipsis body-1">{{ item.daysRemaining }}</span>
          <span class="text-ellipsis body-1" v-else>{{ $t("general.unlimited") }}</span>
        </template>
        <!--Warning-->
        <template v-slot:item.warning="{ item }">
          <v-tooltip color="lightSecondary lighten-4" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-on="on" v-if="item.warningColor" :color="item.warningColor" class="me-2">warning</v-icon>
            </template>
            <span v-if="item.warningText" class="black--text body-1">{{ item.warningText }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>

import ResizeObserver from 'resize-observer-polyfill';
import clientService from '../../../services/client.service';
import * as _ from 'lodash';
import moment from 'moment';

export default {
  name: "ClientsLastLogin",
  components:{
  },
  props: {
   
  },
  data() {
    return {
      loading: false,
      observer: null,
      clientsData: [],
      filteredClientsData: [],
      search: '',
      hideNonLoggedIn: false,
      showOnlyTrial: false,
      lastLoginHeaders: [
        {
          text: this.$t("general.client"),
          value: "name",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("general.user"),
          value: "username",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("table.usersOfClient.lastLogin"),
          value: "lastLogin",
          align: "start",
          sortable: true,
          width: "120px"
        },
        {
          text: this.$t("dashboards.daysWithoutLogin"),
          value: "daysPassed",
          align: "center",
          sortable: true,
          width: "100"
        },
        {
          text: this.$t("dashboards.trialDays"),
          value: "daysRemaining",
          align: "center",
          sortable: true,
          width: "100"
        },
        {
          text: this.$t("general.warning"),
          value: "warning",
          align: "center",
          sortable: false,
        },
      ],
    }
  },
  computed: {
  },
  async created() {
    await this.loadLastLogins();
    this.filteredClientsData = this.clientsData;
  },
  mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(this.$el);
  },
  methods: {
    onResize () {
      this.$emit('heightChange', this.$el.clientHeight)
    },
    async loadLastLogins() {
      const loginsData = await clientService.getLastLoginOfActiveClients();

      //For sorting data (when lastLogin is null to be placed at the top of the array)
      const noDate = moment('01/01/1900 00:00', 'DD/MM/yyyy HH:mm').toDate(); 
      //Sort from longest to least since last login
      this.clientsData = _.orderBy(loginsData.data, (client) => {
        if (client.lastLogin === null) return noDate;
        else return moment(client.lastLogin, 'DD/MM/yyyy HH:mm').toDate();
      }, 'asc');
      
      //Calculate the days between last login and today
      const today = moment();
      this.clientsData.map(client => {
        if (client.lastLogin) client.daysPassed = today.diff(moment(client.lastLogin, 'DD/MM/yyyy HH:mm'), 'days');
        else client.daysPassed = null;
        client.warningColor = this.getWarningColor(client);
        client.warningText = this.getWarningText(client);
      });
    },
    getWarningColor(item) {
      if (!item.lastLogin) return 'red';
      if (item.daysPassed > 30) return 'orange';
      if (item.daysPassed <= 30 && item.daysPassed >= 15) return 'yellow darken-2';
      else return null;
    },
    getWarningText(item) {
      if (!item.lastLogin) return 'No connection';
      if (item.daysPassed > 30) return '> 30 days';
      if (item.daysPassed <= 30 && item.daysPassed >= 15) return '> 15 days';
      else return null;
    },
    getUsernameText(item) {
      if (item.username) return item.username.split("@")[0];
    },
    onInputChange() {
      this.filteredClientsData = this.clientsData.filter(item => {
        if (this.hideNonLoggedIn && this.showOnlyTrial) return !!item.lastLogin && !!item.daysRemaining;
        else if (this.hideNonLoggedIn && !this.showOnlyTrial) return !!item.lastLogin;
        else if (this.showOnlyTrial && !this.hideNonLoggedIn) return !!item.daysRemaining;
        else return true;
      });
    }
  },
  destroyed () {
    delete this.observer;
  }
};
</script>



// WEBPACK FOOTER //
// src/views/dashboard/widgets/ClientsLastLogin.vue